import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Truck as TruckIcon, Trash2, Send, HardHat, QrCode } from "lucide-react";
import { toast } from "sonner";
import { TruckQRDialog } from "@/components/TruckQRDialog";
import ContractorsPanel from "@/components/ContractorsPanel";

const STATUS_COLOR = {
  available: "border-white text-ink",
  loading: "border-[#D4D4D8] text-[#D4D4D8]",
  en_route: "border-[#71717A] text-[#A1A1AA]",
  on_site: "border-primary-orange text-primary-orange",
  returning: "border-[#3F3F46] text-[#71717A]",
  maintenance: "border-red-700 text-red-400",
};

const empty = { number: "", rego: "", capacity_m3: 8, driver_id: null, driver_name: "", service_due: "", status: "available", photo_url: "", fuel_pct: "", tyre_hours: "", engine_hours: "", contractor_id: null };

export default function Fleet() {
  const { plantId } = usePlant();
  const nav = useNavigate();
  const [qrTruck, setQrTruck] = useState(null);
  const [list, setList] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [inviteTruck, setInviteTruck] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "" });
  const [inviting, setInviting] = useState(false);
  const [drivers, setDrivers] = useState([]);

  const load = () => api.get(`/trucks${plantId ? `?plant_id=${plantId}` : ""}`).then((r) => setList(r.data));
  const loadContractors = () => api.get("/contractors").then((r) => setContractors(r.data)).catch(() => {});
  const loadDrivers = () => api.get("/auth/users").then((r) => setDrivers((r.data || []).filter((u) => u.role === "driver"))).catch(() => {});
  useEffect(() => { load(); loadContractors(); loadDrivers(); /* eslint-disable-next-line */ }, [plantId]);
  const contractorById = Object.fromEntries(contractors.map((c) => [c.id, c]));

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => {
    // Older trucks linked by typed name only — resolve the matching driver account
    // so the pick-list preselects correctly and Save persists a solid driver_id link.
    let driver_id = t.driver_id || null;
    if (!driver_id && t.driver_name) {
      const match = drivers.find((d) => (d.name || "").trim().toLowerCase() === (t.driver_name || "").trim().toLowerCase());
      if (match) driver_id = match.id;
    }
    setEditing(t);
    setForm({ ...empty, ...t, driver_id, service_due: t.service_due || "", contractor_id: t.contractor_id || null });
    setOpen(true);
  };
  const save = async () => {
    try {
      // Normalise optional numeric fields — blank → null so pydantic Optional[int] accepts them
      const clean = { ...form };
      ["fuel_pct", "tyre_hours", "engine_hours"].forEach((k) => {
        if (clean[k] === "" || clean[k] === undefined || Number.isNaN(clean[k])) clean[k] = null;
      });
      if (clean.photo_url === "") clean.photo_url = null;
      if (!clean.contractor_id) clean.contractor_id = null;
      if (editing) await api.put(`/trucks/${editing.id}`, clean); else await api.post("/trucks", { ...clean, plant_id: plantId });
      toast.success("Saved"); setOpen(false); load(); loadContractors();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete truck?")) return; await api.delete(`/trucks/${id}`); toast.success("Deleted"); load(); };

  const openInvite = (t) => {
    setInviteTruck(t);
    setInviteForm({ email: "", name: t.driver_name || "" });
  };
  const sendInvite = async () => {
    if (!inviteForm.email || !inviteForm.name) return toast.error("Email and name are required");
    setInviting(true);
    try {
      const r = await api.post(`/trucks/${inviteTruck.id}/invite-driver`, inviteForm);
      if (r.data.sandbox && r.data.recipient !== r.data.intended_recipient) {
        toast.success(`Sandbox: invite for ${inviteTruck.number} routed to ${r.data.recipient} (would have gone to ${r.data.intended_recipient})`);
      } else {
        toast.success(`Invite sent to ${r.data.recipient} — they can now log into /driver`);
      }
      setInviteTruck(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="fleet-page">
      <div className="flex items-end justify-between">
        <div><div className="label-tag">FLEET</div><h1 className="heading text-3xl font-black uppercase">Trucks <span className="text-primary-orange">& Drivers</span></h1></div>
        <Button data-testid="truck-qr-sheet-btn" variant="outline" onClick={() => nav("/fleet/qr-sheet")} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold mr-2"><QrCode className="w-4 h-4 mr-1" />QR sheet</Button>
        <Button data-testid="new-truck-btn" onClick={openNew} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"><Plus className="w-4 h-4 mr-1" />New Truck</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((t) => {
          const fuel = t.fuel_pct != null ? Number(t.fuel_pct) : null;
          const fuelTone = fuel == null ? "bg-surface-2" : fuel < 20 ? "bg-red-500" : fuel < 40 ? "bg-amber-500" : "bg-emerald-500";
          return (
          <div key={t.id} onClick={() => openEdit(t)} data-testid={`truck-card-${t.number}`} className="card-v2 kpi-hover p-4 cursor-pointer">
            <div className="flex items-start gap-3">
              {t.photo_url ? (
                <img src={t.photo_url} alt={t.number} className="w-14 h-14 rounded-lg object-cover border border-industrial shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-surface-2 border border-industrial flex items-center justify-center shrink-0">
                  <TruckIcon className="w-7 h-7 text-primary-orange" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="heading font-black text-lg leading-none">{t.number}</div>
                    <div className="text-xs text-muted-industrial mono mt-1">{t.rego || "—"}</div>
                  </div>
                  <span className={`mono text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-md ${STATUS_COLOR[t.status]}`}>{t.status?.replace("_", " ")}</span>
                </div>
                {t.contractor_id && contractorById[t.contractor_id] && (
                  <div className="mt-1 inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider text-sky-400" data-testid={`truck-contractor-badge-${t.number}`}>
                    <HardHat className="w-3 h-3" /> {contractorById[t.contractor_id].name}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
              <div>
                <div className="label-tag">Driver</div>
                <div className="truncate">{t.driver_name || "—"}</div>
              </div>
              <div>
                <div className="label-tag">Capacity</div>
                <div className="mono">{t.capacity_m3} m³</div>
              </div>
              <div>
                <div className="label-tag">Service</div>
                <div className="mono">{t.service_due || "—"}</div>
              </div>
            </div>

            {(fuel != null || t.engine_hours != null || t.tyre_hours != null) && (
              <div className="mt-3 pt-3 border-t border-industrial space-y-2">
                {fuel != null && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="label-tag">Fuel</span>
                      <span className="mono text-[11px]">{fuel}%</span>
                    </div>
                    <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
                      <div className={`h-full ${fuelTone} transition-all`} style={{ width: `${Math.max(0, Math.min(100, fuel))}%` }} />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 text-[11px] mono">
                  {t.engine_hours != null && (
                    <div><span className="text-muted-industrial">Hrs</span> <span className="text-ink">{t.engine_hours}</span></div>
                  )}
                  {t.tyre_hours != null && (
                    <div><span className="text-muted-industrial">Tyres</span> <span className="text-ink">{t.tyre_hours} hr</span></div>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-industrial">
              <button
                data-testid={`invite-driver-${t.number}`}
                onClick={(e) => { e.stopPropagation(); openInvite(t); }}
                className="mono text-[10px] uppercase text-primary-orange hover:underline inline-flex items-center gap-1"
                title="Email a driver a login link for the /driver portal"
              >
                <Send className="w-3 h-3" /> Invite driver
              </button>
              <div className="flex items-center gap-3">
                <button
                  data-testid={`truck-qr-${t.number}`}
                  onClick={(e) => { e.stopPropagation(); setQrTruck(t); }}
                  className="mono text-[10px] uppercase text-sky-400 hover:underline inline-flex items-center gap-1"
                  title="Permanent QR for this truck — give it to the pump crew paired with it"
                ><QrCode className="w-3 h-3" /> Truck QR</button>
                <button onClick={(e) => { e.stopPropagation(); del(t.id); }} className="text-muted-industrial hover:text-primary-orange"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      <TruckQRDialog open={!!qrTruck} onOpenChange={(v) => !v && setQrTruck(null)} truck={qrTruck} />

      <ContractorsPanel contractors={contractors} reload={() => { loadContractors(); load(); }} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg">
          <DialogHeader><DialogTitle className="heading uppercase">{editing ? "Edit" : "New"} Truck</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-tag">Number</Label><Input data-testid="truck-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Rego</Label><Input value={form.rego} onChange={(e) => setForm({ ...form, rego: e.target.value })} className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Capacity m³</Label><Input type="number" step="0.5" value={form.capacity_m3} onChange={(e) => setForm({ ...form, capacity_m3: parseFloat(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" /></div>
            <div>
              <Label className="label-tag">Driver</Label>
              <select
                data-testid="truck-driver-select"
                value={form.driver_id || ""}
                onChange={(e) => {
                  const d = drivers.find((x) => x.id === e.target.value);
                  setForm({ ...form, driver_id: d ? d.id : null, driver_name: d ? d.name : "" });
                }}
                className="w-full h-10 rounded-none bg-transparent border border-industrial mono text-sm px-2 focus:outline-none focus:border-primary-orange"
              >
                <option value="" className="bg-surface">— Unassigned —</option>
                {form.driver_name && !drivers.some((d) => d.id === form.driver_id) && (
                  <option value="" disabled className="bg-surface">{form.driver_name} (not a driver login)</option>
                )}
                {drivers.map((d) => (
                  <option key={d.id} value={d.id} className="bg-surface">{d.name}</option>
                ))}
              </select>
            </div>
            <div><Label className="label-tag">Service Due</Label><Input type="date" value={form.service_due} onChange={(e) => setForm({ ...form, service_due: e.target.value })} className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Photo URL</Label><Input value={form.photo_url || ""} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://…" className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Fuel %</Label><Input type="number" min="0" max="100" value={form.fuel_pct ?? ""} onChange={(e) => setForm({ ...form, fuel_pct: e.target.value === "" ? "" : parseInt(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Engine Hours</Label><Input type="number" min="0" value={form.engine_hours ?? ""} onChange={(e) => setForm({ ...form, engine_hours: e.target.value === "" ? "" : parseInt(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Tyre Hours Left</Label><Input type="number" min="0" value={form.tyre_hours ?? ""} onChange={(e) => setForm({ ...form, tyre_hours: e.target.value === "" ? "" : parseInt(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" /></div>
            <div className="col-span-2">
              <Label className="label-tag flex items-center gap-1"><HardHat className="w-3 h-3 text-sky-400" /> Contractor (owner of this truck)</Label>
              <select
                data-testid="truck-contractor-select"
                value={form.contractor_id || ""}
                onChange={(e) => setForm({ ...form, contractor_id: e.target.value || null })}
                className="w-full h-10 rounded-none bg-transparent border border-industrial mono text-sm px-3 text-ink"
              >
                <option value="">— Plant-owned truck —</option>
                {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div className="text-[10px] mono text-muted-industrial mt-1">Manual link. Trucks also auto-link when the rego matches one the contractor registered.</div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-truck-btn" onClick={save} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!inviteTruck} onOpenChange={(v) => { if (!v) setInviteTruck(null); }}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-md" data-testid="invite-driver-dialog">
          <DialogHeader><DialogTitle className="heading uppercase">Invite driver — {inviteTruck?.number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-industrial leading-relaxed">
              We&apos;ll create a login for this driver (or refresh an existing one) and email them the URL, credentials, and setup instructions for the Driver Portal on their phone. GPS tracking starts as soon as they hit <span className="text-primary-orange mono">Depart Plant</span>.
            </div>
            <div>
              <Label className="label-tag">Driver name</Label>
              <Input
                data-testid="invite-driver-name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="Chris Wong"
                className="rounded-none bg-transparent border-industrial mono"
              />
            </div>
            <div>
              <Label className="label-tag">Driver email</Label>
              <Input
                data-testid="invite-driver-email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="chris.wong@gadtech.com.au"
                className="rounded-none bg-transparent border-industrial mono"
              />
            </div>
            <div className="text-[10px] mono uppercase text-muted-industrial">
              A random temp password will be generated and included in the email. The driver should change it after first login.
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="send-invite-btn"
              onClick={sendInvite}
              disabled={inviting}
              className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"
            >
              <Send className="w-4 h-4 mr-1" /> {inviting ? "Sending…" : "Send Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
